import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, Music4 } from "lucide-react";

// Custom minimal audio player for MP3/WAV/OGG
function StandardAudio({ src, title }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime);
      setProgress(a.duration ? a.currentTime / a.duration : 0);
    };
    const onLoad = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * a.duration;
  };

  const fmt = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = String(Math.floor(s % 60)).padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div className="w-full border border-fg/15 bg-bg" data-testid="audio-player">
      <div className="flex items-center gap-4 p-5">
        <button
          onClick={toggle}
          className="w-12 h-12 rounded-full bg-fg text-bg flex items-center justify-center hover:bg-cinabre transition-colors shrink-0"
          data-testid="audio-play"
          aria-label={playing ? "Pause" : "Lecture"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-serif text-lg truncate">{title}</div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60 shrink-0">
              {fmt(current)} / {fmt(duration)}
            </div>
          </div>
          <div
            className="mt-3 h-[3px] bg-fg/15 cursor-pointer"
            onClick={seek}
          >
            <div className="h-full bg-cinabre" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
        <Volume2 size={16} className="text-fg/50 shrink-0" />
      </div>
      <audio ref={ref} src={src} preload="metadata" />
    </div>
  );
}

// MIDI player using html-midi-player (loaded via CDN in index.html)
function MidiAudio({ src, title }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => {
      if (window.customElements && window.customElements.get("midi-player")) {
        setReady(true);
        clearInterval(iv);
      }
    }, 200);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="w-full border border-fg/15 bg-bg" data-testid="midi-player">
      <div className="flex items-center gap-4 p-5 flex-wrap">
        <div className="w-12 h-12 rounded-full bg-fg text-bg flex items-center justify-center shrink-0">
          <Music4 size={18} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-serif text-lg">{title || "Composition MIDI"}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/60 mt-1">MIDI · Playback synthétisé</div>
        </div>
      </div>
      <div className="px-5 pb-5">
        {ready ? (
          <div dangerouslySetInnerHTML={{
            __html: `<midi-player src="${src}" sound-font style="width:100%"></midi-player>
                     <midi-visualizer type="piano-roll" src="${src}" style="width:100%; height:120px; margin-top:12px"></midi-visualizer>`
          }} />
        ) : (
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 py-4">Chargement du moteur MIDI…</div>
        )}
      </div>
    </div>
  );
}

export default function AudioPlayer({ src, title, kind }) {
  if (!src) return null;
  const inferMidi = /\.midi?($|\?)/i.test(src) || kind === "midi";
  if (inferMidi) return <MidiAudio src={src} title={title} />;
  return <StandardAudio src={src} title={title} />;
}
